import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Breadcrumbs, Tabs, Avatar, Badge, Button, EmptyState, Select, Table, Edit01, Trash01,
} from '@opendate-app/setlist';
import CrmDualModeTabs from '../../shared/components/CrmDualModeTabs';
import { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import CrmEnrichmentLogCard from '../../shared/components/CrmEnrichmentLogCard';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import OrganizationFormModal from '../../Organizations/components/OrganizationFormModal';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import DonationsTab from '../../shared/components/DonationsTab';
import PledgesTab from '../../shared/components/pledges/PledgesTab';
import LogDonationModal from '../../ContactsDetail/components/LogDonationModal';
import SoftCreditsModal from '../../shared/components/SoftCreditsModal';
import FairMarketValueModal from '../../shared/components/FairMarketValueModal';
import SoftCreditsReceivedCard from '../../shared/components/SoftCreditsReceivedCard';
import SocialCard from '../../ContactsDetail/components/sidebar/SocialCard';
import FilesCard from '../../ContactsDetail/components/sidebar/FilesCard';
import TagsCard from '../../ContactsDetail/components/sidebar/TagsCard';
import ActivityFeed from '../../ContactsDetail/components/overview/ActivityFeedCard';
import { buildActivityFeed, feedCounts, mapNoteForFeed } from '../../ContactsDetail/components/overview/activityFeed';
import useSoftCredits from '../../ContactsDetail/hooks/useSoftCredits';
import useDonations from '../../ContactsDetail/hooks/useDonations';
import useTags from '../../ContactsDetail/hooks/useTags';
import CrmCustomFieldsCard from '../../shared/components/CrmCustomFieldsCard';
import crmLabels from '../../shared/utils/crmLabels';

const axios = require('axios').default;

const formatDealAmount = (amount) => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDealCloseDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const titleizeOrDash = (value) => {
  if (!value) return '—';
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const contactTypeNames = (contact) => {
  const types = contact.contact_types || [];
  if (types.length === 0) return contact.contact_type;
  return types.map((type) => (type.archived ? `${type.name} (archived)` : type.name)).join(', ');
};

function OrganizationDetailsPage({
  team,
  organization,
  csrfToken,
  currentUser,
  contactTypes = [],
  customFieldDefinitions = [],
  donationCustomFieldDefinitions = [],
  canLogDonations = false,
}) {
  const labels = crmLabels(team);
  const orgSingularLower = labels.organization.singular.toLowerCase();
  if (!team || !organization) return null;
  const contactTypeOptions = contactTypes.map((type) => ({ value: type.id, label: type.name }));

  const [currentOrganization, setCurrentOrganization] = useState(organization);
  const [contacts, setContacts] = useState([]);
  const dealsEnabled = team.deals_enabled !== false;
  const [deals, setDeals] = useState([]);
  const [events, setEvents] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [eventsPage, setEventsPage] = useState(1);
  const eventsPerPage = 10;
  const [activeTab, setActiveTab] = useState('overview');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [showLogDonationModal, setShowLogDonationModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');

  const donationsHook = useDonations({
    team,
    contact: currentOrganization,
    csrfToken,
    enabled: canLogDonations,
    donationsUrl: `/teams/${team.id}/crm/organizations/${currentOrganization.id}/donations.json`,
  });

  const softCreditsEnabled = team.soft_credits_enabled !== false;
  const softCreditsHook = useSoftCredits({
    team,
    csrfToken,
    enabled: softCreditsEnabled,
    softCreditsUrl: `/teams/${team.id}/crm/organizations/${currentOrganization.id}/soft_credits.json`,
  });
  // The donation row whose "Add/Edit Soft Credits" modal is open (null = closed).
  const [softCreditsModalDonation, setSoftCreditsModalDonation] = useState(null);
  const [fairMarketValueDonation, setFairMarketValueDonation] = useState(null);

  const [notes, setNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteErrors, setNoteErrors] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);

  const [orgInfoExpanded, setOrgInfoExpanded] = useState(false);
  const [avatarHovered, setAvatarHovered] = useState(false);
  const avatarFileInputRef = useRef(null);

  const [inlineField, setInlineField] = useState(null);
  const [inlineValue, setInlineValue] = useState('');
  const [inlineSubmitting, setInlineSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [hoverField, setHoverField] = useState(null);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactModalTab, setContactModalTab] = useState('existing');

  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addCompanyName, setAddCompanyName] = useState(currentOrganization.name || '');
  const [addContactTypeIds, setAddContactTypeIds] = useState([]);
  const [addCompanyTypeaheadOpen, setAddCompanyTypeaheadOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addErrors, setAddErrors] = useState([]);

  const [attachSearch, setAttachSearch] = useState('');
  const [attachResults, setAttachResults] = useState([]);
  const [attachSelectedId, setAttachSelectedId] = useState(null);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachSubmitting, setAttachSubmitting] = useState(false);
  const [attachError, setAttachError] = useState('');

  const tagsProps = useTags({
    team, taggableType: 'Organization', taggableId: currentOrganization.id, csrfToken,
  });

  const name = currentOrganization.name || '(No name)';
  const primaryContact = currentOrganization.email || currentOrganization.phone || '--';
  const orgInitials = (name.match(/\b\w/g) || ['?']).slice(0, 2).join('').toUpperCase();
  const subtitle = currentOrganization.website_url || primaryContact;

  const formatCurrency = (amount) => (amount == null ? '—' : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount));
  const formatDate = (dateStr) => (dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—');

  const activityFeed = buildActivityFeed({
    notes: notes.map(mapNoteForFeed),
    donations: (donationsHook.donations || []).map((d) => ({
      id: d.id, amount: d.amount, description: d.name, created_at: d.date,
    })),
    attachments,
    teamId: team.id,
  });
  const activityCounts = feedCounts(activityFeed);
  const openDeals = deals.filter((d) => d.status === 'open');

  const handleAvatarFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) handleAvatarUpload(file);
  };

  const attachmentsUrl = `/teams/${team.id}/crm/organizations/${currentOrganization.id}/attachments.json`;

  const handleUploadAttachment = async (file) => {
    if (!file || !team || !team.id || !currentOrganization.id || !csrfToken) return;
    try {
      setAttachmentsError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(attachmentsUrl, formData);
      setAttachments((prev) => [response.data, ...prev]);
    } catch (err) {
      const messages = err && err.response && err.response.data;
      const message = Array.isArray(messages) ? messages.join(', ') : 'Failed to upload attachment';
      setAttachmentsError(message);
      toast.error(message);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!attachment || !team || !team.id || !currentOrganization.id || !csrfToken) return;
    try {
      setAttachmentsError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/crm/organizations/${currentOrganization.id}/attachments/${attachment.id}.json`);
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
    } catch (err) {
      setAttachmentsError('Failed to delete attachment');
      toast.error('Failed to delete attachment');
    }
  };

  useEffect(() => {
    setCurrentOrganization(organization);
  }, [organization]);

  useEffect(() => {
    if (!team || !team.id || !organization || !organization.id || !csrfToken) return;

    const fetchPageData = async () => {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      try {
        const [contactsRes, dealsRes, eventsRes, filterValuesRes] = await Promise.all([
          axios.get(`/teams/${team.id}/crm/fans.json`, {
            params: {
              'q[fan_organizations_organization_id_eq]': organization.id,
              per_page: 100,
            },
          }),
          dealsEnabled ? axios.get(`/teams/${team.id}/crm/deals.json`, {
            params: {
              'q[organization_id_eq]': organization.id,
              per_page: 100,
            },
          }) : { data: { deals: [] } },
          axios.get(`/teams/${team.id}/crm/organizations/${organization.id}/events.json`),
          axios.get(`/teams/${team.id}/crm/fans/filter_values`),
        ]);

        setContacts(contactsRes.data.contacts || []);
        setDeals(dealsRes.data.deals || []);
        setEvents(eventsRes.data.events || []);

        const filterValues = filterValuesRes.data || {};
        setCompanyNames((filterValues.company_names || []).map((c) => c.value));
      } catch (e) {
        toast.error(`Failed to load ${orgSingularLower} details.`);
      }
    };

    fetchPageData();
  }, [team && team.id, organization && organization.id, csrfToken]);

  useEffect(() => {
    if (!team || !team.id || !organization || !organization.id || !csrfToken) return;

    const fetchNotes = async () => {
      try {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

        const response = await axios.get(`/teams/${team.id}/crm/notes.json`, {
          params: { notable_type: 'Organization', notable_id: organization.id },
        });
        setNotes(response.data || []);
      } catch (err) {
        toast.error('Failed to load notes');
      }
    };

    fetchNotes();
  }, [team, organization, csrfToken]);

  useEffect(() => {
    if (!team || !team.id || !currentOrganization || !currentOrganization.id) return undefined;

    let cancelled = false;
    const loadAttachments = async () => {
      try {
        setAttachmentsLoading(true);
        setAttachmentsError('');
        const response = await axios.get(`/teams/${team.id}/crm/organizations/${currentOrganization.id}/attachments.json`);
        if (!cancelled) setAttachments(response.data || []);
      } catch (err) {
        if (!cancelled) {
          setAttachmentsError('Failed to load attachments');
          toast.error('Failed to load attachments');
        }
      } finally {
        if (!cancelled) setAttachmentsLoading(false);
      }
    };

    loadAttachments();
    return () => { cancelled = true; };
  }, [team && team.id, currentOrganization && currentOrganization.id]);

  const refreshNotes = async () => {
    try {
      const response = await axios.get(`/teams/${team.id}/crm/notes.json`, {
        params: { notable_type: 'Organization', notable_id: organization.id },
      });
      setNotes(response.data || []);
    } catch (err) {
      toast.error('Failed to refresh notes.');
    }
  };

  const handleCreateNote = async (body) => {
    if (!team || !team.id || !csrfToken || !organization || !organization.id) {
      throw new Error('Missing context to create note');
    }
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    await axios.post(`/teams/${team.id}/crm/notes.json`, {
      note: {
        title: deriveNoteTitle(body),
        body,
        notable_type: 'Organization',
        notable_id: organization.id,
      },
    });
    await refreshNotes();
  };

  const canSubmitNoteForm = noteBody && noteBody.trim().length > 0;

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!canSubmitNoteForm) return;
    if (!team || !team.id || !csrfToken || !organization || !organization.id) return;

    try {
      setNoteSubmitting(true);
      setNoteErrors([]);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const noteData = {
        note: {
          title: noteTitle.trim() || null,
          body: noteBody.trim(),
          notable_type: 'Organization',
          notable_id: organization.id,
        },
      };

      if (editingNoteId) {
        await axios.patch(`/teams/${team.id}/crm/notes/${editingNoteId}.json`, noteData);
      } else {
        await axios.post(`/teams/${team.id}/crm/notes.json`, noteData);
      }

      await refreshNotes();
      setShowNoteModal(false);
      setEditingNoteId(null);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setNoteErrors(resp.data);
      } else {
        setNoteErrors([editingNoteId ? 'Failed to update note' : 'Failed to create note']);
      }
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleEditNote = (note) => {
    setNoteTitle(note.title || '');
    const plainBody = (note.sanitized_body || note.body || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    setNoteBody(plainBody);
    setEditingNoteId(note.id);
    setNoteErrors([]);
    setShowNoteModal(true);
  };

  const handleDeleteNote = async (note) => {
    if (!team || !team.id || !csrfToken || !note || !note.id) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm('Are you sure you want to delete this note?');
    if (!confirmed) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/crm/notes/${note.id}.json`);
      await refreshNotes();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Failed to delete note');
    }
  };

  const startEditing = () => setFormModalOpen(true);

  const handleOrganizationSaved = (saved) => {
    setCurrentOrganization((prev) => ({ ...prev, ...saved }));
  };

  const startInlineEdit = (field) => {
    setInlineField(field);
    setInlineError('');

    let value = '';
    if (field === 'phone') value = currentOrganization.phone || '';
    if (field === 'website_url') value = currentOrganization.website_url || '';
    if (field === 'facebook_url') value = currentOrganization.facebook_url || '';
    if (field === 'instagram_url') value = currentOrganization.instagram_url || '';
    if (field === 'twitter_url') value = currentOrganization.twitter_url || '';
    if (field === 'linkedin_url') value = currentOrganization.linkedin_url || '';

    setInlineValue(value);
  };

  const handleSaveInlineField = async () => {
    if (!inlineField || !team || !team.id || !currentOrganization.id || !csrfToken) {
      setInlineField(null);
      return;
    }

    try {
      setInlineSubmitting(true);
      setInlineError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const payload = {
        organization: {
          [inlineField]: inlineValue || null,
        },
      };

      const response = await axios.patch(
        `/teams/${team.id}/crm/organizations/${currentOrganization.id}.json`,
        payload,
      );

      setCurrentOrganization(response.data || currentOrganization);
      setInlineField(null);
    } catch (e) {
      setInlineError('Incomplete URL');
    } finally {
      setInlineSubmitting(false);
    }
  };

  const [enriching, setEnriching] = useState(false);
  const [enrichRefreshKey, setEnrichRefreshKey] = useState(0);

  const handleEnrich = async () => {
    if (!team || !team.id || !currentOrganization.id || !csrfToken || enriching) return;

    setEnriching(true);
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.post(`/teams/${team.id}/crm/organizations/${currentOrganization.id}/enrich.json`);
      toast.success('Enrichment started — this can take up to a minute.');

      const startingAvatar = currentOrganization.avatar_thumb_url;
      const showUrl = `/teams/${team.id}/crm/organizations/${currentOrganization.id}.json`;
      let attempts = 0;

      const poll = async () => {
        attempts += 1;
        try {
          const res = await axios.get(showUrl);
          if (res.data && res.data.avatar_thumb_url && res.data.avatar_thumb_url !== startingAvatar) {
            setCurrentOrganization(res.data);
            setEnrichRefreshKey((k) => k + 1);
            toast.success(`${labels.organization.singular} enriched`);
            setEnriching(false);
            return;
          }
        } catch (e) {
          // transient — keep polling until the attempt cap
        }

        if (attempts >= 15) {
          setEnrichRefreshKey((k) => k + 1);
          setEnriching(false);
          toast.info('Still processing — refresh shortly to see updates.');
          return;
        }
        setTimeout(poll, 4000);
      };

      setTimeout(poll, 4000);
    } catch (e) {
      setEnriching(false);
      toast.error('Failed to start enrichment');
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!team || !team.id || !currentOrganization.id || !csrfToken) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const formData = new FormData();
      formData.append('organization[photo]', file);

      const response = await axios.patch(
        `/teams/${team.id}/crm/organizations/${currentOrganization.id}.json`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      setCurrentOrganization(response.data || currentOrganization);
      toast.success('Photo updated');
    } catch (e) {
      toast.error('Failed to update photo');
    }
  };

  const openContactModal = () => {
    setContactModalTab('existing');

    setAddFirstName('');
    setAddLastName('');
    setAddEmail('');
    setAddPhone('');
    setAddCompanyName(currentOrganization.name || '');
    setAddContactTypeIds([]);
    setAddCompanyTypeaheadOpen(false);
    setAddErrors([]);
    setAddSubmitting(false);

    setAttachSearch('');
    setAttachResults([]);
    setAttachSelectedId(null);
    setAttachError('');
    setAttachLoading(false);
    setAttachSubmitting(false);

    setShowContactModal(true);
  };

  const closeContactModal = () => {
    if (addSubmitting || attachSubmitting) return;
    setShowContactModal(false);
  };

  const canSubmitCreateContactForm = addFirstName && addFirstName.trim().length > 0;

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!canSubmitCreateContactForm) return;
    if (!team || !team.id || !csrfToken) return;

    if (!addFirstName.trim()) {
      setAddErrors(['First name is required']);
      return;
    }

    try {
      setAddSubmitting(true);
      setAddErrors([]);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const payload = {
        fan: {
          first_name: addFirstName.trim(),
          last_name: addLastName.trim() || null,
          email: addEmail.trim() || null,
          phone_number: addPhone.trim() || null,
          company_name: addCompanyName.trim() || null,
          contact_type_ids: addContactTypeIds,
        },
      };

      const fanResponse = await axios.post(
        `/teams/${team.id}/crm/fans.json`,
        payload,
      );

      const created = fanResponse.data;
      const attachResponse = await axios.post(
        `/teams/${team.id}/crm/organizations/${currentOrganization.id}/fan_organizations.json`,
        { fan_id: created.id },
      );

      setContacts((prev) => [attachResponse.data, ...prev]);
      setShowContactModal(false);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setAddErrors(resp.data);
      } else {
        setAddErrors(['Failed to create contact']);
      }
    } finally {
      setAddSubmitting(false);
    }
  };


  const handleAttachSearch = async () => {
    if (!team || !team.id) return;

    const query = attachSearch.trim();
    if (!query) {
      setAttachResults([]);
      setAttachSelectedId(null);
      setAttachError('');
      return;
    }

    try {
      setAttachLoading(true);
      setAttachError('');

      const response = await axios.get(`/teams/${team.id}/crm/fans/autocomplete`, {
        params: {
          name: query,
          omit_ids: contacts.map((c) => c.id),
        },
      });

      const results = response.data || [];
      setAttachResults(results);
      if (!results.find((r) => r.id === attachSelectedId)) {
        setAttachSelectedId(null);
      }
    } catch (err) {
      setAttachError('Failed to search contacts');
    } finally {
      setAttachLoading(false);
    }
  };

  const handleAttachContact = async (e) => {
    e.preventDefault();
    if (!team || !team.id || !csrfToken || !attachSelectedId) return;

    try {
      setAttachSubmitting(true);
      setAttachError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const response = await axios.post(
        `/teams/${team.id}/crm/organizations/${currentOrganization.id}/fan_organizations.json`,
        { fan_id: attachSelectedId },
      );

      const attached = response.data;
      setContacts((prev) => {
        const filtered = prev.filter((c) => c.id !== attached.id);
        return [attached, ...filtered];
      });

      setShowContactModal(false);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setAttachError(resp.data.join(', '));
      } else {
        setAttachError('Failed to attach contact');
      }
    } finally {
      setAttachSubmitting(false);
    }
  };

  const handleDetachContact = async (event, contact) => {
    event.stopPropagation();
    if (!team || !team.id || !csrfToken || !contact || !contact.id) return;

    const confirmed = window.confirm(
      `Remove this contact from this ${orgSingularLower}? They will remain in your contacts list.`,
    );
    if (!confirmed) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(
        `/teams/${team.id}/crm/organizations/${currentOrganization.id}/fan_organizations/${contact.id}.json`,
      );

      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (err) {
      toast.error('Failed to detach contact');
    }
  };

  return (
    <CrmPageLayout team={team} activeSidebarKey="organizations">
          <div className="card mb-3 cdp-main-card">
            <div className="card-body">
              <div style={{ marginBottom: 12 }}>
                <Breadcrumbs items={[
                  { label: labels.organization.plural, href: `/teams/${team.id}/crm/organizations` },
                  { label: `${labels.organization.singular} Details` },
                ]} />
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-start" style={{ gap: 16 }}>
                <div className="d-flex align-items-center" style={{ gap: 16, minWidth: 0, flex: '1 1 280px' }}>
                  <div
                    style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}
                    onMouseEnter={() => setAvatarHovered(true)}
                    onMouseLeave={() => setAvatarHovered(false)}
                  >
                    <Avatar
                      initials={orgInitials}
                      src={currentOrganization.avatar_thumb_url || undefined}
                      alt={name}
                      size="lg"
                    />
                    <input
                      ref={avatarFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAvatarFileChange}
                    />
                    <button
                      type="button"
                      aria-label="Change photo"
                      onClick={() => avatarFileInputRef.current && avatarFileInputRef.current.click()}
                      onFocus={() => setAvatarHovered(true)}
                      onBlur={() => setAvatarHovered(false)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        border: 'none',
                        padding: 0,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: avatarHovered ? 1 : 0,
                        transition: 'opacity 0.15s ease-in-out',
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fal fa-camera" style={{ color: 'var(--sl-text-white)', fontSize: 20 }} aria-hidden="true" />
                    </button>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                      <h1 className="mb-0" style={{ fontSize: 24, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{name}</h1>
                      {currentOrganization.org_type && <Badge>{currentOrganization.org_type}</Badge>}
                    </div>
                    {subtitle && <div className="text-break" style={{ color: 'var(--sl-text-secondary)', fontSize: 14 }}>{subtitle}</div>}
                  </div>
                </div>
                <div className="d-flex align-items-center flex-shrink-0" style={{ gap: 10 }}>
                  <Button variant="secondary" onClick={handleEnrich} disabled={enriching}>
                    {enriching ? 'Enriching…' : 'Enrich'}
                  </Button>
                  <Button variant="primary" icon={Edit01} onClick={startEditing}>{`Edit ${orgSingularLower}`}</Button>
                </div>
              </div>

              <div style={{ height: 20 }} />

              <Tabs
                aria-label={`${labels.organization.singular} detail sections`}
                collapseBelow={0}
                overflow
                value={activeTab}
                onChange={setActiveTab}
                items={[
                  { value: 'overview', label: 'Overview' },
                  { value: 'events', label: 'Events' },
                  ...(dealsEnabled ? [{ value: 'deals', label: labels.deal.plural }] : []),
                  { value: 'contacts', label: labels.contact.plural },
                  { value: 'donations', label: 'Donations' },
                  { value: 'pledges', label: 'Pledges' },
                ]}
              />

              {activeTab === 'overview' && (
                <>
                  <hr style={{ margin: '0 0 20px 0', border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
                  <div className="row">
                    <div className="col-lg-8">
                      <div className="d-flex flex-column" style={{ gap: 20 }}>
                        <div className="cdp-card">
                          <div className="cdp-card-header-row">
                            <h2 className="cdp-card-title">People</h2>
                            <button type="button" className="btn btn-setlist-primary btn-sm d-inline-flex align-items-center" onClick={openContactModal}>
                              <span className="me-1">+</span>
                              <span>Add contact</span>
                            </button>
                          </div>
                          {contacts.length === 0 ? (
                            <div className="cdp-files-empty">No contacts yet.</div>
                          ) : (
                            <>
                              {contacts.slice(0, 5).map((contact) => {
                                const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '(No name)';
                                return (
                                  <div className="cdp-side-row" key={contact.id}>
                                    <a href={`/teams/${team.id}/crm/fans/${contact.id}`} className="cdp-affiliation-name">{fullName}</a>
                                    <span className="cdp-side-row-value">{contactTypeNames(contact) || contact.email || '—'}</span>
                                  </div>
                                );
                              })}
                              {contacts.length > 5 && (
                                <div className="cdp-card-footer cdp-card-footer--center">
                                  <Button variant="link-color" size="sm" onClick={() => setActiveTab('contacts')}>View all {contacts.length}</Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <CrmCustomFieldsCard
                          teamId={team?.id}
                          customFieldDefinitions={customFieldDefinitions}
                          customFieldValues={currentOrganization.custom_field_values}
                        />

                        <div className="cdp-overview-activity-spacer">
                          <ActivityFeed
                            feed={activityFeed}
                            counts={activityCounts}
                            loading={donationsHook.loading}
                            onCreateNote={handleCreateNote}
                            onEditNote={handleEditNote}
                            onDeleteNote={handleDeleteNote}
                            onUploadAttachment={handleUploadAttachment}
                            showTickets={false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 cdp-overview-sidebar">
                      <div className="cdp-card">
                        <h2 className="cdp-card-title">{`${labels.organization.singular} Info`}</h2>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">Website</span>
                          <span className="cdp-side-row-value">
                            {currentOrganization.website_url ? (
                              <a href={currentOrganization.website_url} target="_blank" rel="noopener noreferrer">{currentOrganization.website_url}</a>
                            ) : '--'}
                          </span>
                        </div>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">Phone</span>
                          <span className="cdp-side-row-value">{currentOrganization.human_phone || currentOrganization.phone || '--'}</span>
                        </div>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">Email</span>
                          <span className="cdp-side-row-value">
                            {currentOrganization.email ? <a href={`mailto:${currentOrganization.email}`}>{currentOrganization.email}</a> : '--'}
                          </span>
                        </div>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">{`${labels.organization.singular} Type`}</span>
                          <span className="cdp-side-row-value">{currentOrganization.org_type || '--'}</span>
                        </div>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">Industry</span>
                          <span className="cdp-side-row-value">{currentOrganization.industry || '--'}</span>
                        </div>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">City</span>
                          <span className="cdp-side-row-value">{currentOrganization.city || '--'}</span>
                        </div>
                        <div className="cdp-side-row">
                          <span className="cdp-side-row-label">State</span>
                          <span className="cdp-side-row-value">{currentOrganization.state || '--'}</span>
                        </div>
                        {orgInfoExpanded && (
                          <>
                            <div className="cdp-side-row">
                              <span className="cdp-side-row-label">Address</span>
                              <span className="cdp-side-row-value">{currentOrganization.address_1 || '--'}</span>
                            </div>
                            <div className="cdp-side-row">
                              <span className="cdp-side-row-label">Address 2</span>
                              <span className="cdp-side-row-value">{currentOrganization.address_2 || '--'}</span>
                            </div>
                            <div className="cdp-side-row">
                              <span className="cdp-side-row-label">ZIP</span>
                              <span className="cdp-side-row-value">{currentOrganization.zip || '--'}</span>
                            </div>
                            <div className="cdp-side-row">
                              <span className="cdp-side-row-label">Source</span>
                              <span className="cdp-side-row-value">{currentOrganization.source || '--'}</span>
                            </div>
                          </>
                        )}
                        <div className="cdp-card-footer cdp-card-footer--center">
                          <Button variant="link-gray" size="sm" onClick={() => setOrgInfoExpanded((v) => !v)}>
                            {orgInfoExpanded ? 'See Less' : 'See More'}
                          </Button>
                        </div>
                      </div>

                      <SocialCard
                        urls={{
                          facebook_url: currentOrganization.facebook_url,
                          instagram_url: currentOrganization.instagram_url,
                          twitter_url: currentOrganization.twitter_url,
                          linkedin_url: currentOrganization.linkedin_url,
                        }}
                        editingField={inlineField}
                        hoverField={hoverField}
                        editValues={{ [inlineField]: inlineValue }}
                        onEditValueChange={(field, value) => setInlineValue(value)}
                        onHover={setHoverField}
                        onStartEdit={startInlineEdit}
                        onSave={handleSaveInlineField}
                        onCancelEdit={() => setInlineField(null)}
                        submitting={inlineSubmitting}
                        error={inlineError}
                      />

                      {dealsEnabled && (
                      <div className="cdp-card">
                        <div className="cdp-card-header-row">
                          <h2 className="cdp-card-title">Open Deals</h2>
                          {deals.length > 0 && (
                            <Button variant="link-color" size="sm" onClick={() => setActiveTab('deals')}>View all</Button>
                          )}
                        </div>
                        {openDeals.length === 0 ? (
                          <div className="cdp-files-empty">No open deals.</div>
                        ) : (
                          openDeals.slice(0, 5).map((deal) => (
                            <div className="cdp-side-row" key={deal.id}>
                              <a href={`/teams/${team.id}/crm/deals/${deal.id}`} className="cdp-affiliation-name">{deal.name || '—'}</a>
                              <span className="cdp-side-row-value">{formatCurrency(deal.amount)}</span>
                            </div>
                          ))
                        )}
                      </div>
                      )}

                      <CrmEnrichmentLogCard
                        teamId={team.id}
                        organizationId={currentOrganization.id}
                        refreshKey={enrichRefreshKey}
                      />

                      <TagsCard {...tagsProps} currentUser={currentUser} />

                      <FilesCard
                        attachments={attachments}
                        loading={attachmentsLoading}
                        error={attachmentsError}
                        onUpload={handleUploadAttachment}
                        onDelete={handleDeleteAttachment}
                        canManage
                      />

                      <div className="cdp-rail-bottom-spacer">
                        <div className="cdp-card">
                          <h2 className="cdp-card-title">Record</h2>
                          <div className="cdp-side-row">
                            <span className="cdp-side-row-label">Created</span>
                            <span className="cdp-side-row-value">{formatDate(currentOrganization.created_at)}</span>
                          </div>
                          <div className="cdp-side-row">
                            <span className="cdp-side-row-label">{labels.contact.plural}</span>
                            <span className="cdp-side-row-value">{currentOrganization.contacts_count ?? contacts.length}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

            {activeTab === 'events' && (() => {
                const eventsTotalPages = Math.ceil(events.length / eventsPerPage);
                const eventsStartIndex = (eventsPage - 1) * eventsPerPage;
                const paginatedEvents = events.slice(eventsStartIndex, eventsStartIndex + eventsPerPage);

                const formatEventDate = (dateStr) => {
                  if (!dateStr) return '—';
                  const date = new Date(dateStr);
                  return date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });
                };

                const formatClassification = (classification) => {
                  if (!classification) return '—';
                  return classification
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                };

                return (
                  <div className="mt-3">
                    <div className="card">
                      <div className="card-body p-0">
                        <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                          <h2 className="h6 mb-0">Events</h2>
                        </div>

                        <Table
                          columns={[
                            {
                              key: 'title',
                              header: 'Event Name',
                              primary: true,
                              render: (event) => (
                                <Button as="a" href={`/teams/${team.id}/confirms/${event.id}`} variant="link-color">
                                  {event.title || '—'}
                                </Button>
                              ),
                            },
                            { key: 'venue', header: 'Venue', render: (event) => event.venue_name || '—' },
                            { key: 'date', header: 'Event Date', render: (event) => formatEventDate(event.start_time) },
                            { key: 'status', header: 'Status', render: (event) => formatClassification(event.calendar_classification) },
                          ]}
                          data={paginatedEvents}
                          getRowKey={(event) => event.id}
                          empty={<EmptyState size="sm" title="No events yet." />}
                        />

                        {events.length > 0 && (
                          <div className="px-4 py-3 border-top">
                            <TeamPagination
                              currentPage={eventsPage}
                              totalPages={eventsTotalPages}
                              totalItems={events.length}
                              pageSize={eventsPerPage}
                              onPageChange={(page) => {
                                if (page >= 1 && page <= eventsTotalPages) {
                                  setEventsPage(page);
                                }
                              }}
                              itemLabel="events"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {activeTab === 'deals' && dealsEnabled && (
                <div className="mt-3">
                  <div className="card">
                    <div className="card-body p-0">
                      <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0">{labels.deal.plural}</h2>
                      </div>

                      <Table
                        columns={[
                          {
                            key: 'name',
                            header: 'Name',
                            primary: true,
                            render: (deal) => (
                              <Button as="a" href={`/teams/${team.id}/crm/deals/${deal.id}`} variant="link-color">
                                {deal.name || '—'}
                              </Button>
                            ),
                          },
                          { key: 'amount', header: 'Amount', render: (deal) => formatDealAmount(deal.amount) },
                          { key: 'stage', header: 'Stage', render: (deal) => titleizeOrDash(deal.stage) },
                          { key: 'type', header: 'Type', render: (deal) => titleizeOrDash(deal.deal_type) },
                          { key: 'close_date', header: 'Close Date', render: (deal) => formatDealCloseDate(deal.close_date) },
                        ]}
                        data={deals}
                        getRowKey={(deal) => deal.id}
                        empty={<EmptyState size="sm" title={`No ${labels.deal.plural.toLowerCase()} yet.`} />}
                      />
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'contacts' && (
                <div className="mt-3">
                  <div className="card">
                    <div className="card-body p-0">
                      <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0">{labels.contact.plural}</h2>
                        <div className="d-flex align-items-center">
                          <button
                            type="button"
                            className="btn btn-setlist-primary btn-sm d-inline-flex align-items-center"
                            onClick={openContactModal}
                          >
                            <span className="me-1">+</span>
                            <span>Add contact</span>
                          </button>
                        </div>
                      </div>

                      <Table
                        columns={[
                          {
                            key: 'name',
                            header: 'Name',
                            primary: true,
                            render: (contact) => (
                              <Button as="a" href={`/teams/${team.id}/crm/fans/${contact.id}`} variant="link-color">
                                {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '(No name)'}
                              </Button>
                            ),
                          },
                          { key: 'email', header: 'Email', render: (contact) => contact.email || '—' },
                          { key: 'phone', header: 'Phone', render: (contact) => contact.human_phone || contact.phone || '—' },
                          { key: 'contact_type', header: 'Contact Type', render: (contact) => contactTypeNames(contact) || '—' },
                          {
                            key: 'actions',
                            header: 'Actions',
                            align: 'right',
                            render: (contact) => (contact.id && (
                              <Button
                                variant="link-destructive"
                                size="sm"
                                icon={Trash01}
                                onClick={(event) => handleDetachContact(event, contact)}
                                aria-label={`Detach contact from ${orgSingularLower}`}
                              />
                            )),
                          },
                        ]}
                        data={contacts}
                        getRowKey={(contact) => contact.id}
                        empty={<EmptyState size="sm" title="No contacts yet." />}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'donations' && (
                <DonationsTab
                  team={team}
                  donationsHook={donationsHook}
                  canLogDonations={canLogDonations}
                  onLogDonation={() => setShowLogDonationModal(true)}
                  statementHref={(year) => `/teams/${team.id}/crm/organizations/${currentOrganization.id}/donations/statement.pdf?year=${year}`}
                  showSoftCredits={softCreditsEnabled}
                  onAddSoftCredit={(donation) => setSoftCreditsModalDonation(donation)}
                  onEditFairMarketValue={(donation) => setFairMarketValueDonation(donation)}
                  afterDonations={softCreditsEnabled && (
                    <SoftCreditsReceivedCard softCreditsHook={softCreditsHook} subjectLabel={`this ${orgSingularLower}`} />
                  )}
                />
              )}

              {activeTab === 'pledges' && (
                <PledgesTab
                  team={team}
                  organization={currentOrganization}
                  canManage={currentUser.can_manage_pledges}
                />
              )}
            </div>
          </div>

      {showLogDonationModal && (
        <LogDonationModal
          team={team}
          organization={currentOrganization}
          csrfToken={csrfToken}
          customFieldDefinitions={donationCustomFieldDefinitions}
          onClose={() => setShowLogDonationModal(false)}
          onRecorded={() => {
            setShowLogDonationModal(false);
            donationsHook.reload();
          }}
        />
      )}

      {fairMarketValueDonation && (
        <FairMarketValueModal
          donation={fairMarketValueDonation}
          path={`/teams/${team.id}/crm/organizations/${currentOrganization.id}/donations/${fairMarketValueDonation.id}`}
          csrfToken={csrfToken}
          onClose={() => setFairMarketValueDonation(null)}
          onSaved={() => {
            donationsHook.reload();
            toast.success('Benefits saved');
          }}
        />
      )}

      {softCreditsModalDonation && (
        <SoftCreditsModal
          team={team}
          basePath={`/teams/${team.id}/crm/organizations/${currentOrganization.id}/donations/${softCreditsModalDonation.id}/soft_credits`}
          omitOrganizationIds={[currentOrganization.id]}
          csrfToken={csrfToken}
          onClose={() => setSoftCreditsModalDonation(null)}
          onChanged={() => {
            donationsHook.reload();
            softCreditsHook.reload();
          }}
        />
      )}

      {showContactModal && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 720 }}>
            <div className="team-modal-header">
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="flex-grow-1">
                  <h3 className="team-modal-title mb-2">Add contact</h3>
                  <CrmDualModeTabs
                    activeMode={contactModalTab}
                    onChange={setContactModalTab}
                    newLabel="Create new"
                    existingLabel="Add existing"
                  />
                </div>
                <button
                  type="button"
                  className="team-modal-close"
                  onClick={closeContactModal}
                  aria-label="Close"
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>

            {contactModalTab === 'new' && (
              <form onSubmit={handleCreateContact}>
                <div className="team-modal-body">
                  {addErrors.length > 0 && (
                    <div className="alert alert-danger py-2">
                      <ul className="mb-0">
                        {addErrors.map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">First name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addFirstName}
                        onChange={(e) => setAddFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Last name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addLastName}
                        onChange={(e) => setAddLastName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={addEmail}
                        onChange={(e) => setAddEmail(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={addPhone}
                        onChange={(e) => setAddPhone(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 position-relative">
                      <label className="form-label">Company name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addCompanyName}
                        onChange={(e) => {
                          setAddCompanyName(e.target.value);
                          setAddCompanyTypeaheadOpen(true);
                        }}
                        onFocus={() => setAddCompanyTypeaheadOpen(true)}
                        onBlur={() => {
                          setTimeout(() => setAddCompanyTypeaheadOpen(false), 150);
                        }}
                        autoComplete="off"
                      />
                      {addCompanyTypeaheadOpen && companyNames && companyNames.length > 0 && (
                        <div
                          className="dropdown-menu show w-100 shadow-sm"
                          style={{ maxHeight: 200, overflowY: 'auto' }}
                        >
                          {companyNames
                            .filter((name) => {
                              if (!addCompanyName) return true;
                              return (name || '')
                                .toLowerCase()
                                .includes(addCompanyName.toLowerCase());
                            })
                            .slice(0, 10)
                            .map((name) => (
                              <button
                                key={name}
                                type="button"
                                className="dropdown-item"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setAddCompanyName(name);
                                  setAddCompanyTypeaheadOpen(false);
                                }}
                              >
                                {name}
                              </button>
                            ))}
                          {companyNames.length === 0 && (
                            <div className="dropdown-item text-muted small">No companies</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Contact type</label>
                      <Select
                        aria-label="Contact type"
                        placeholder="Select…"
                        multiple
                        searchable
                        clearable
                        options={contactTypeOptions}
                        value={addContactTypeIds}
                        onChange={(value) => setAddContactTypeIds(value || [])}
                      />
                    </div>
                  </div>
                </div>
                <div className="team-modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeContactModal}
                    disabled={addSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canSubmitCreateContactForm || addSubmitting}
                  >
                    {addSubmitting ? 'Saving…' : 'Create contact'}
                  </button>
                </div>
              </form>
            )}

            {contactModalTab === 'existing' && (
              <form onSubmit={handleAttachContact}>
                <div className="team-modal-body">
                  {attachError && (
                    <div className="alert alert-danger py-2 mb-2">{attachError}</div>
                  )}

                  <p className="text-muted small">
                    Search for an existing contact to associate with this {orgSingularLower}. Attaching
                    will update the contact&apos;s company name to match “{currentOrganization.name}”.
                  </p>

                  <div className="mb-3 d-flex gap-2">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search contacts by name"
                      value={attachSearch}
                      onChange={(e) => setAttachSearch(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleAttachSearch}
                      disabled={attachLoading}
                    >
                      {attachLoading ? 'Searching…' : 'Search'}
                    </button>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '1%' }}>Select</th>
                          <th>Name</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!attachLoading && attachResults.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-muted text-center py-3">
                              {attachSearch.trim()
                                ? 'No matching contacts found.'
                                : 'Enter a search term and click Search to find contacts.'}
                            </td>
                          </tr>
                        )}
                        {attachResults.map((result) => (
                          <tr key={result.id}>
                            <td>
                              <input
                                type="radio"
                                className="team-checkbox"
                                checked={attachSelectedId === result.id}
                                onChange={() => setAttachSelectedId(result.id)}
                                aria-label={`Select ${result.name}`}
                              />
                            </td>
                            <td>{result.name}</td>
                            <td className="text-muted small">
                              {result.title_email_preview || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="team-modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeContactModal}
                    disabled={attachSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={attachSubmitting || !attachSelectedId}
                  >
                    {attachSubmitting ? 'Attaching…' : 'Attach contact'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 560 }}>
            <div className="team-modal-header">
              <div className="d-flex justify-content-between align-items-start w-100">
                <h3 className="team-modal-title mb-0">{editingNoteId ? 'Edit note' : 'Add note'}</h3>
                <button
                  type="button"
                  className="team-modal-close"
                  onClick={() => {
                    setShowNoteModal(false);
                    setEditingNoteId(null);
                  }}
                  aria-label="Close"
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveNote}>
              <div className="team-modal-body">
                {noteErrors.length > 0 && (
                  <div className="alert alert-danger py-2 mb-3">
                    <ul className="mb-0">
                      {noteErrors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Title (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="Enter note title"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Note</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Enter note content"
                    required
                  />
                </div>
              </div>

              <div className="team-modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowNoteModal(false);
                    setEditingNoteId(null);
                  }}
                  disabled={noteSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canSubmitNoteForm || noteSubmitting}
                >
                  {noteSubmitting ? 'Saving…' : (editingNoteId ? 'Update note' : 'Save note')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <OrganizationFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        organization={currentOrganization}
        team={team}
        csrfToken={csrfToken}
        customFieldDefinitions={customFieldDefinitions}
        onSaved={handleOrganizationSaved}
      />
    </CrmPageLayout>
  );
}

export default OrganizationDetailsPage;
